import React from 'react';
import i18n from '@/i18n/config';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea
} from '@heroui/react';
import { RiCloseCircleLine } from '@remixicon/react';
import { toast } from 'react-toastify';

import type { ExperimentItem } from '@/types';
import { useAtom } from 'jotai';
import { setExperimentStatus } from '@/store';

const MAX_REASON_LENGTH = 2048;

interface CancelExperimentModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  experiment: ExperimentItem;
  onSuccess?: () => void;
}

export function CancelExperimentModal({
  isOpen,
  onOpenChange,
  experiment,
  onSuccess
}: CancelExperimentModalProps) {
  const [cancellationReason, setCancellationReason] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [, setStatusAtom] = useAtom(setExperimentStatus);

  React.useEffect(() => {
    if (isOpen) {
      setCancellationReason('');
    }
  }, [isOpen]);

  const canSubmit = cancellationReason.trim().length > 0;

  const handleSubmit = React.useCallback(async () => {
    if (!canSubmit) return;
    try {
      setIsSubmitting(true);
      await setStatusAtom({
        id: experiment.id,
        status: 'cancelled',
        cancellation_reason: cancellationReason.trim()
      });
      toast.success(i18n.t('copy.experiment_cancelled_successfully'));
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to cancel experiment:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [cancellationReason, canSubmit, experiment.id, setStatusAtom, onOpenChange, onSuccess]);

  const handleClose = React.useCallback(() => {
    setCancellationReason('');
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <RiCloseCircleLine className="text-danger h-5 w-5" />
                <span>{i18n.t('modals.experiment.cancelTitle')}</span>
              </div>
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <p className="text-default-600 text-sm">
                  {i18n.t('modals.experiment.cancelDescription', { name: experiment.name })}
                </p>
                <Textarea
                  label={i18n.t('copy.reason_for_cancellation_required')}
                  placeholder={i18n.t('copy.why_is_this_experiment_being_cancelled')}
                  value={cancellationReason}
                  onValueChange={setCancellationReason}
                  variant="bordered"
                  minRows={3}
                  maxLength={MAX_REASON_LENGTH}
                  description={`${cancellationReason.length}/${MAX_REASON_LENGTH} characters`}
                  isRequired
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose} isDisabled={isSubmitting}>
                {i18n.t('common.close')}
              </Button>
              <Button
                color="danger"
                variant="flat"
                isLoading={isSubmitting}
                onPress={handleSubmit}
                isDisabled={!canSubmit}
              >
                {i18n.t('modals.experiment.cancelTitle')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
